import type { ColorMode } from './brand';

/**
 * The colours a zone kind can be drawn in on the Zones map. A kind stores the key (`zone_kinds.color`),
 * never a hex, so the whole set can be retuned here. Muted and warm like the rest of the brand, but
 * far enough apart to tell a port from a depot at a glance. Each has a shade per colour mode, since the
 * map's basemap follows the app's: each clears 3:1 against that mode's surface (WCAG 1.4.11, non-text
 * contrast), asserted by zone-kind-colors.spec.ts.
 */
export const ZONE_KIND_COLORS = {
  blue: { label: 'Blue', light: '#3D63C9', dark: '#8FA8EC' },
  navy: { label: 'Navy', light: '#34466E', dark: '#9CAAD0' },
  teal: { label: 'Teal', light: '#2F7C78', dark: '#6FBDB6' },
  sage: { label: 'Sage', light: '#4F7A3E', dark: '#93BC7F' },
  olive: { label: 'Olive', light: '#6E6E2F', dark: '#B9B96C' },
  ochre: { label: 'Ochre', light: '#9A6B16', dark: '#E2B462' },
  sand: { label: 'Sand', light: '#8C6D45', dark: '#CFB28C' },
  terracotta: { label: 'Terracotta', light: '#B24A36', dark: '#E88A73' },
  rust: { label: 'Rust', light: '#8A3F24', dark: '#C8785A' },
  rose: { label: 'Rose', light: '#B04F66', dark: '#E691A5' },
  plum: { label: 'Plum', light: '#7D4F80', dark: '#C499C7' },
  slate: { label: 'Slate', light: '#5F6B7A', dark: '#A8B2BF' },
  charcoal: { label: 'Charcoal', light: '#4A4540', dark: '#BFB8AF' },
} as const satisfies Record<string, { label: string } & Record<ColorMode, string>>;

export type ZoneKindColor = keyof typeof ZONE_KIND_COLORS;
export const ZONE_KIND_COLOR_KEYS = Object.keys(ZONE_KIND_COLORS) as [ZoneKindColor, ...ZoneKindColor[]];

/** A new kind's colour until an admin picks one, and the fallback for a zone whose kind isn't in the catalogue. */
export const DEFAULT_ZONE_KIND_COLOR: ZoneKindColor = 'slate';
